<template>
  <new-edit-page
    :is-loading="loading"
    :page-title="pageTitle"
    :submit-button-label="submitButtonLabel"
    :submit-button-disabled="submitButtonDisabled"
    @submit="saveTemplate"
  >
    <template v-slot:leftSideContent>
      <form class="management-form" ref="reservationTemplateForm">
        <div class="input-field-container medium">
          <label for="label" class="required">{{ $tc("Label", 1) }}</label>
          <input
            v-model="template.label"
            type="text"
            id="label"
            required
            class="input-field"
          />
        </div>

        <div class="input-field-container medium">
          <label class="required">{{ $tc("Owner") }}</label>
          <treeselect
            v-model="template.owner_id"
            :options="ownerOptions"
            :load-options="loadOwnerOptions"
            :normalizer="ownerOptionsNormalizer"
            :clearable="false"
            :disabled="actionType === 'edit'"
            required
          />
        </div>

        <div class="input-field-container large">
          <label class="required"> {{ $tc("Layer", 2) }} </label>
          <treeselect
            v-model="template.accepted_layers"
            :options="layerOptions"
            :default-expand-level="1"
            :clearable="false"
            :disabled="actionType === 'edit'"
            required
            multiple
          />
        </div>

        <div class="input-field-container medium" v-if="!(actionType === 'edit')">
          <label class="required"> {{ $t("Status") }} </label>
          <treeselect
            v-model="template.status"
            :options="statusOptions"
            :load-options="loadStatusOptions"
            :normalizer="normalizer"
            :clearable="false"
            required
            multiple
          />
        </div>

        <div class="input-field-container medium">
          <label for="index">{{ $t("Index") }}</label>
          <input
            v-model="template.index"
            type="number"
            step="1"
            id="index"
            class="input-field"
            :max="1000"
          />
        </div>

        <div class="input-field-container medium">
          <label>{{ $t("Active") }}</label>
          <v-switch
            color="accent"
            v-model="template.active"
            :label="template.active ? $t('Yes') : $t('No')"
          />
        </div>
      </form>
    </template>

    <template v-slot:rightSideHeader>
      <v-layout row justify-end>
        <v-btn @click="openRequestFieldForm" x-small text icon color="accent">
          <v-icon>add_circle</v-icon>
        </v-btn>
      </v-layout>
    </template>

    <template v-slot:rightSideContent>
      <reservation-template-fields-list
        :request-fields="template.request_fields"
        :action-type="actionType"
        :key="updated"
        @edit-item="openRequestFieldForm"
        @delete-item="deleteRequestField"
      />

      <reservation-template-field-form
        v-if="requestFieldForm.isOpen"
        :open="requestFieldForm.isOpen"
        :item-index="requestFieldForm.index"
        :original-item="requestFieldForm.data"
        :selected-fields="template.request_fields"
        @add="addRequestField"
        @edit="editRequestField"
        @close="closeRequestFieldForm"
      />
    </template>
  </new-edit-page>
</template>

<script>
import { mapActions } from "vuex";
import NewEditPage from "@/components/Management/NewEditPage/NewEditPage";
import ManagementButton from "@/components/Management/ManagementButton";
import _ from "lodash";
import { listsMixin } from "@/mixins/listsMixin";
import ReservationTemplateFieldsList from "@/components/AssetReservationManagement/Templates/ReservationTemplateFieldsList";
import ReservationTemplateFieldForm from "@/components/AssetReservationManagement/Templates/ReservationTemplateFieldForm";

export default {
  name: "ReservationTemplateCreateEdit",

  components: {
    ReservationTemplateFieldForm,
    ReservationTemplateFieldsList,
    ManagementButton,
    NewEditPage
  },

  mixins: [listsMixin],

  data() {
    return {
      /**
       * Define if the page is loading
       */
      loading: false,
      /**
       * Define the values to create/edit a template
       */
      template: {
        label: "",
        owner_id: null,
        accepted_layers: [],
        index: null,
        active: true,
        status: [],
        request_fields: []
      },

      /**
       * Owner options that can be used as a basis for creating/editing
       */
      ownerOptions: null,

      /**
       * Stores the necessary information for when the fields form is opened
       * {Boolean} isOpen - Defines when the form is opened
       * {index} index - Determines the position of the field in the list, if the user is editing
       * {Object} data - Field data if the user is editing
       */
      requestFieldForm: {
        isOpen: false,
        index: -1,
        data: {}
      },

      /**
       * Used to update components with current information
       */
      updated: 0,

      /**
       * Status options that can be used as a basis for creating/editing
       */
      statusOptions: null
    };
  },

  computed: {
    /**
     * If action type is equal edit, determines the uuid of the selected template that is defined in the route
     * @return {String}
     */
    templateUuid() {
      if (this.actionType === "edit") {
        return this.$route.params.uuid;
      }
    },

    /**
     * Type of action
     * @values create, edit
     */
    actionType() {
      let routeName = this.$route.name;
      if (routeName === "reservation-template-edit") {
        return "edit";
      } else if (routeName === "reservation-template-create") {
        return "create";
      }
    },

    /**
     * Determines the title of the page
     * @return {String}
     */
    pageTitle() {
      return this.actionType === "create"
        ? this.$t("NewTemplate")
        : this.$t("UpdateTemplate");
    },

    /**
     * Determines the title of the submit button
     * @return {String}
     */
    submitButtonLabel() {
      return this.actionType === "create" ? this.$t("Create") : this.$t("Save");
    },

    /**
     * If the submit button is disabled or not.
     */
    submitButtonDisabled() {
      if (this.template.index > 1000) {
        this.$toasted.clear();
        this.$toasted.global.toast_error(
          this.$t("Index") + this.$t("ValueLowerThan") + 1000
        );
      }
      return (
        this.template.label === "" ||
        !this.template.owner_id ||
        this.template.accepted_layers.length === 0 ||
        this.template.status.length === 0 ||
        this.template.index > 1000
      );
    },

    /**
     * Layer options that can be used as a basis for creating a new one
     */
    layerOptions() {
      return [
        {
          id: "all",
          label: this.$i18n.t("All"),
          children: [
            { id: "fc_pathway", label: this.$i18n.tc("Pathway", 2) },
            { id: "fc_pole", label: this.$i18n.tc("Pole", 2) },
            { id: "fc_manhole", label: this.$i18n.tc("Manhole", 2) },
            { id: "fc_toby_box", label: this.$i18n.tc("TobyBox", 2) },
            { id: "fc_cabinet", label: this.$i18n.tc("Cabinet", 2) }
          ]
        }
      ];
    }
  },

  mounted() {
    if (this.actionType === "edit") {
      this.getTemplate();
    }
  },

  methods: {
    ...mapActions([
      "fetchAssetReservationTemplateByUUID",
      "createAssetReservationTemplate",
      "editAssetReservationTemplate",
    ]),

    /**
     * Get template by the uuid that is defined in the current page route
     * @return {Promise<void>}
     */
    async getTemplate() {
      this.loading = true;
      let appends = [
        "active_asset_reservations_count",
        "owner_name",
        "request_fields"
      ];

      try {
        let template = await this.fetchAssetReservationTemplateByUUID({
          templateUUID: this.templateUuid,
          appends: appends.join(",")
        });
        this.template = {
          label: template.art_label,
          owner_id: template.art_owner_id,
          accepted_layers: template.art_accepted_layers.split(","),
          index: template.art_index,
          active: template.art_active,
          request_fields: template.request_fields,
          status: template.art_status
        };
      } catch (e) {
        await this.$router.replace("/error");
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Loads list of owner options
     * @param callback
     * @returns {Promise<void>}
     */
    async loadOwnerOptions({ callback }) {
      this.ownerOptions = await this.getList(
        "list_organization",
        this.template.owner_id ? [this.template.owner_id] : [],
        false
      );
      callback();
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param node - option being normalized.
     * @returns {{id: *, label: *}}
     */
    ownerOptionsNormalizer(node) {
      return {
        id: node.or_id,
        label: node.or_nom
      };
    },

    /**
     * Request the BE to edit or create the template.
     * @returns {Promise<void>}
     * @success - Return to previous page
     * @error
     */
    async saveTemplate() {
      this.loading = true;
      let dataToSend = _.cloneDeep(this.template);
      dataToSend.accepted_layers = dataToSend.accepted_layers.join(",");
      try {
        if (this.actionType === "create") {
          await this.createAssetReservationTemplate(dataToSend);
        } else if (this.actionType === "edit") {
          await this.editAssetReservationTemplate({
            uuid: this.templateUuid,
            data: dataToSend
          });
        }
        this.$router.back();
      } catch (e) {
        console.log(e);
        if (e.response.data.e.errorInfo[0] === "22003") {
          this.$toasted.global.toast_error(e.response.data.e.errorInfo[2]);
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * It opens the form of the fields, and if the user is editing, it sets the index and date values of the requestFieldForm variable
     * @param index
     */
    openRequestFieldForm(index = null) {
      if (index !== null && index > -1) {
        this.requestFieldForm.data = this.template.request_fields[index];
        this.requestFieldForm.index = index;
      }
      this.requestFieldForm.isOpen = true;
    },

    /**
     * Set the requestFieldForm variable to initial values
     */
    closeRequestFieldForm() {
      this.requestFieldForm.isOpen = false;
      this.requestFieldForm.data = {};
      this.requestFieldForm.index = -1;
    },

    /**
     * Add new field
     * @param newRequestField
     */
    addRequestField(newRequestField) {
      this.template.request_fields.push(newRequestField);
      this.closeRequestFieldForm();
    },

    /**
     * Edit selected field
     * @param updatedRequestField
     */
    editRequestField(updatedRequestField) {
      this.template.request_fields[
        this.requestFieldForm.index
      ] = updatedRequestField;

      this.closeRequestFieldForm();
      this.updated++;
    },

    /**
     * Delete selected field
     */
    deleteRequestField() {
      this.template.request_fields.splice(this.requestFieldForm.index, 1);
    },

    /**
     * Loads list of status options
     */
    async loadStatusOptions({ callback }) {
      this.statusOptions = await this.getList(
        "list_avancement",
        this.status ? [this.status] : [],
        false
      );
      callback();
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param {Object} node - option being normalized.
     * @returns {{id, label: (null|*|string)}}
     */
    normalizer(node) {
      return {
        id: node.code,
        label: node.client_label ? node.client_label : node.libelle
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AssetReservationManagement/Templates/ReservationTemplateCreateEdit.vue